import { useDebug } from '@/utils/useDebugLog';
import React, {
  useRef,
  useEffect,
  useState,
} from 'react';
import gsap from 'gsap';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';

// Styles
import className from 'classnames/bind';
import styles from './CursorFollower.module.scss';
const cx = className.bind(styles);

interface CursorFollowerProps {
  propClassName?: string;
  container: React.RefObject<HTMLElement>;
  circleText: string | null;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  iconClass?: string;
  externalPos?: { x: number, y: number } | null;
  isVisible: boolean | false;
  mouseActive: boolean | false;
  onClick?: () => void;
  pulseOnChange?: boolean;
  // drop the default mix-blend-mode: difference (e.g. the mobile badge, where it hurts legibility)
  disableMixBlend?: boolean;
}

const CursorFollower: React.FC<CursorFollowerProps> = ({
  propClassName,
  container,
  circleText,
  externalPos,
  isVisible,
  mouseActive,
  icon,
  iconClass,
  onClick,
  pulseOnChange,
  disableMixBlend,
}) => {
  const { log } = useDebug({ name: 'CursorFollower', bgColor: 'lime', enabled: false });

  const { isDesktop } = useViewSize();
  const IconElement = icon;

  // Constants
  const DIAMETER = 96;
  const CONSTANT_ROTATION_DURATION = 7;

  // Element references
  const refRoot = useRef<HTMLDivElement>(null);
  const refSvg = useRef<SVGSVGElement>(null);
  const refText = useRef<SVGTextPathElement>(null);
  const refPop = useRef<HTMLDivElement>(null); // wrapper the meaning-change "pop" scales

  // Other
  const mousePos = useRef({ x: 0, y: 0 });
  const rafId = useRef<number | null>(null);
  const constantRotationTween = useRef<GSAPTween | null>(null);
  const { mounted } = useClientMount();

  // State to track if we are using external position
  // Hero sets a position for this component during its animIn
  const [usingExternal, setUsingExternal] = useState(true);

  // ---------------------------------------- Mouse event handlers

  const animateCursor = () => {
    const { x, y } = mousePos.current;
    if (refRoot.current) {
      gsap.to(refRoot.current, {
        x,
        y,
        duration: 0.5,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!mouseActive || !container.current) return;

    // log('handleMouseMove', { x: e.clientX, y: e.clientY });

    setUsingExternal(false);

    const rect = container.current.getBoundingClientRect();
    mousePos.current = {
      x: e.clientX - rect.left - DIAMETER / 2,
      y: e.clientY - rect.top - DIAMETER / 2,
    };

    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(animateCursor);
  };

  const handleMouseLeave = () => {
    if (!mouseActive || !refRoot.current) return;

    log('handleMouseLeave');

    if (rafId.current) cancelAnimationFrame(rafId.current);
    // Freeze in place: kill the position tween so the follower scales out where
    // it is instead of sliding toward the last cursor point (which made
    // quickly-crossed cards drift toward the cursor in unison).
    gsap.killTweensOf(refRoot.current, 'x,y');

    gsap.to(refRoot.current, {
      scale: 0.1,
      rotation: -45,
      duration: 0.24,
      ease: 'power1.out',
      overwrite: 'auto',
      onComplete: () => {
        gsap.set(refRoot.current, { opacity: 0 });
        constantRotationStop();
      },
    });
  };

  const handleMouseEnter = (e: MouseEvent) => {
    if (!mouseActive || !refRoot.current || !container.current) return;

    log('handleMouseEnter');

    // Appear exactly where the cursor entered — snap there with no animation so
    // it never slides in from wherever it last exited.
    setUsingExternal(false);
    const rect = container.current.getBoundingClientRect();
    const x = e.clientX - rect.left - DIAMETER / 2;
    const y = e.clientY - rect.top - DIAMETER / 2;
    mousePos.current = { x, y };
    if (rafId.current) cancelAnimationFrame(rafId.current);
    gsap.set(refRoot.current, { x, y });

    gsap.to(refRoot.current, {
      scale: 1,
      rotation: 0,
      duration: 0.24,
      ease: 'power1.out',
      overwrite: 'auto',
      onStart: () => {
        gsap.set(refRoot.current, { opacity: 1 });
        constantRotationStart();
      },
    });
  };

  // ---------------------------------------- Constant rotation animation

  const constantRotationStart = () => {
    log('constantRotationStart', { mouseActive, refSvg: !!refSvg.current });
    if (!refSvg.current) return;
    constantRotationTween.current?.kill();
    constantRotationTween.current = gsap.fromTo(
      refSvg.current,
      { rotation: 0 },
      {
        rotation: 360,
        transformOrigin: 'center',
        duration: CONSTANT_ROTATION_DURATION,
        ease: 'none',
        repeat: -1,
      }
    );
  };

  const constantRotationStop = () => {
    constantRotationTween.current?.kill();
    constantRotationTween.current = null;
  };

  // ---------------------------------------- Effects
  const didInitPop = useRef(false);
  useEffect(() => {
    if (!pulseOnChange) return;
    if (!didInitPop.current) {
      didInitPop.current = true; return;
    }
    const el = refPop.current;
    if (!el) return;
    gsap.killTweensOf(el);
    gsap.timeline()
      .to(el, { scale: 0.78, duration: 0.1, ease: 'power2.in' })
      .to(el, { scale: 1, duration: 0.16, ease: 'power2.out' });
  }, [circleText, pulseOnChange]);

  // Hide or show this thing
  useEffect(() => {

    log('isVisible changed', { isVisible });

    if (!isVisible) {
      // Animate out (mirrors mouseLeave) so a slide-change hides the follower smoothly
      // instead of snapping. Position is reset by the next isVisible=true pass.
      gsap.to(refRoot.current, {
        scale: 0.1,
        rotation: -45,
        duration: 0.24,
        ease: 'power1.out',
        overwrite: 'auto',
        onComplete: () => {
          gsap.set(refRoot.current, { opacity: 0 });
          constantRotationStop();
        },
      });
    }

    if (isVisible) {
      // Reset position when animating in. externalPos (intro placement) always
      // wins. Otherwise a non-mouse follower defaults to the bottom-right corner
      // — but in mouse-follow mode the enter handler snaps it to the cursor, so
      // skip the corner default there (it was the source of the slide-in).
      if (externalPos && container.current) {
        gsap.set(refRoot.current, {
          x: externalPos.x - (DIAMETER / 2),
          y: externalPos.y - (DIAMETER / 2),
        });
      } else if (container.current && !mouseActive) {
        const rect = container.current.getBoundingClientRect();
        gsap.set(refRoot.current, {
          x: (rect.width * 0.75) - (DIAMETER / 2),
          y: (rect.height * 0.75) - (DIAMETER / 2),
        });
      }

      // Reset external-position mode so a later externalPos change can re-position,
      // and the next mousemove can flip it back to false as it does today.
      setUsingExternal(true);

      // Animate in
      gsap.to(refRoot.current, {
        scale: 1,
        rotation: 0,
        duration: 0.24,
        ease: 'power1.out',
        overwrite: 'auto',
        onStart: () => {
          gsap.set(refRoot.current, { opacity: 1 });
          constantRotationStart();
        },
      });
    }

  }, [isVisible]);

  // When externalPos changes and usingExternal is true, update position
  useEffect(() => {
    log('externalPos or usingExternal changed', { externalPos, usingExternal, container: !!container.current });
    if (!usingExternal || !externalPos || !container.current) return;
    gsap.set(refRoot.current, {
      x: externalPos.x - (DIAMETER / 2),
      y: externalPos.y - (DIAMETER / 2),
    });
  }, [externalPos, usingExternal]);

  // When mouseActive changes, add or remove event listeners
  useEffect(() => {

    if (!isDesktop) return; // We do not add mouse events on mobile
    if (!container.current) return;

    log('mouseActive changed', { mouseActive });

    // Add events listeners
    if (mouseActive) {
      log('Adding events');
      container.current.addEventListener('mousemove', handleMouseMove);
      container.current.addEventListener('mouseleave', handleMouseLeave);
      container.current.addEventListener('mouseenter', handleMouseEnter);
    }

    // Remove events listeners
    else {
      log('Removing events');
      container.current.removeEventListener('mousemove', handleMouseMove);
      container.current.removeEventListener('mouseleave', handleMouseLeave);
      container.current.removeEventListener('mouseenter', handleMouseEnter);
    }

  }, [mouseActive]);

  // ---------------------------------------- Render

  const [pathData, setPathData] = useState('');

  useEffect(() => {
    if (!refRoot.current) return;

    log('Rendering');

    // Size the gap from the text's  rendered width
    const textRadius = 33; // radius of #circle-path the text rides on
    const textWidth = refText.current?.getComputedTextLength() ?? 0;
    // arc length -> angle (θ = s / r), converted to degrees
    const textAngle = textWidth > 0 ? (textWidth / textRadius) * (180 / Math.PI) : 40;

    const paddingDegrees = 14; // breathing room split across both ends of the text
    const gapSize = textAngle + paddingDegrees;

    // The text appears at the bottom of the circle due to startOffset="50%" on the path starting at top
    const gapStart = 90 - (gapSize / 2); // Center the gap at the bottom (90 degrees)
    const gapEnd = 90 + (gapSize / 2);

    // Create path for circle stroke with gap
    const radius = 36;
    const centerX = 48;
    const centerY = 48;

    // Convert degrees to radians
    const startAngle = (gapEnd * Math.PI) / 180;
    const endAngle = ((gapStart + 360) * Math.PI) / 180;

    const x1 = centerX + radius * Math.cos(startAngle);
    const y1 = centerY + radius * Math.sin(startAngle);
    const x2 = centerX + radius * Math.cos(endAngle);
    const y2 = centerY + radius * Math.sin(endAngle);

    const largeArcFlag = (endAngle - startAngle) > Math.PI ? 1 : 0;

    setPathData(`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`);
  }, [circleText, mounted]);

  // if (!mounted) return null;

  return (
    <div
      className={`${cx('cursor-follower', { visible: isVisible, clickable: !!onClick })} ${propClassName || ''}`}
      ref={refRoot}
      aria-hidden={true}
      tabIndex={-1}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'none', mixBlendMode: disableMixBlend ? 'normal' : undefined }}
    >
      <div ref={refPop} className={cx('pop')}>
        <svg
          fill="none"
          preserveAspectRatio="none"
          role="presentation"
          viewBox="0 0 96 96"
          ref={refSvg}
        >
          <defs>
            <path
              id="circle-path"
              d="M 48,15 A 33,33 0 1,1 47.99,15"
            />
          </defs>
          <g>
            <rect className="main" height="96" rx="48" width="96" />
            <path
              d={pathData}
              stroke="black"
              strokeWidth="1.5"
              fill="none"
            />
            <g>
              <text
                className={cx('circleText')}
                textAnchor="middle"
              >
                <textPath href="#circle-path" startOffset="50%" ref={refText}>
                  {circleText}
                </textPath>
              </text>
            </g>
          </g>
        </svg>
        {IconElement && <IconElement className={cx(['icon', iconClass])} />}
      </div>
    </div>
  );

};

export default CursorFollower;
